import type { ComponentPropsWithoutRef, ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { breakpoints, vars } from '@etg/wings';
import { BrandLogo } from '@eti/components';
import { useSiteContext } from '@eti/providers';
import { css, cx, keyframes } from '@eti/styles';

const { dark, light } = vars.colors.decoration;

const arrowAnimation = keyframes`
  0%, 33% { fill: ${dark}; }
  33%, 100% { fill: ${light}; }
  100% { fill: ${dark}; }
`;

const ThinArrow = (props: ComponentPropsWithoutRef<'svg'>) => (
  <svg viewBox="0 0 34 89" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M13.8411 89L33.67 44.5631L13.9383 0L0 3.20992e-06L0.00913898 2.92092L0.0120676 0.240641L19.7437 44.8017L0.276666 88.4257L0.278463 89H13.8411Z" />
  </svg>
);

const ThickArrow = (props: ComponentPropsWithoutRef<'svg'>) => (
  <svg viewBox="0 0 40 89" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M39.6058 44.5595L19.7771 88.9923L0.44165 88.9922L20.2704 44.5595L0.538616 0.00014796C6.98378 9.19784e-05 13.4289 8.85991e-05 19.8741 0.00014796L39.6058 44.5595Z" />
  </svg>
);

const logoImage = css`
  height: 24px;
  max-width: 88px;

  @media (min-width: ${breakpoints._768}) {
    height: 43px;
    max-width: 139px;
  }
`;

const wrapperStyles = css`
  border: 1px solid ${vars.colors.divider};
  border-radius: 8px;
  display: grid;
  gap: 0;
  grid-template-areas: 'from to';
  grid-template-columns: 1fr 1fr;
  height: 70px;
  max-width: 328px;
  position: relative;
  width: 100%;

  @media (min-width: ${breakpoints._768}) {
    height: 89px;
    max-width: 520px;
    width: 520px;
  }
`;

const logoWrapperStyles = css`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
`;

const lastBoxStyles = css`
  grid-area: to;
  padding-inline-start: 48px;
`;

const arrowStyles = css`
  height: 100%;
  left: 50%;
  position: absolute;
`;

const thinArrowStyles = css`
  animation: ${arrowAnimation} 3s step-end 0s infinite;
  fill: ${light};
  transform: translateX(calc(-50% - 2px));

  [dir='rtl'] & {
    transform: rotate(180deg) translateX(calc(-50% + 24px));
  }

  @media (min-width: ${breakpoints._768}) {
    transform: translateX(calc(-50% - 3px));

    [dir='rtl'] & {
      transform: rotate(180deg) translateX(calc(-50% + 31px));
    }
  }
`;

const firstThickArrowStyles = css`
  animation: ${arrowAnimation} 3s step-end 1s infinite;
  fill: ${light};
  transform: translateX(calc(-50% + 10px));

  [dir='rtl'] & {
    transform: rotate(180deg) translateX(calc(-50% + 41px));
  }

  @media (min-width: ${breakpoints._768}) {
    [dir='rtl'] & {
      transform: rotate(180deg) translateX(calc(-50% + 52px));
    }
  }
`;

const secondThickArrowStyles = css`
  animation: ${arrowAnimation} 3s step-end 2s infinite;
  fill: ${light};
  transform: translateX(calc(-50% + 24px));

  [dir='rtl'] & {
    transform: rotate(180deg) translateX(calc(-50% + 55px));
  }

  @media (min-width: ${breakpoints._768}) {
    transform: translateX(calc(-50% + 28px));

    [dir='rtl'] & {
      transform: rotate(180deg) translateX(calc(-50% + 70px));
    }
  }
`;

const ProgressiveBackgroundSteps = () => {
  const [partnerLogo, setPartnerLogo] = useState<ReactElement | null>(null);
  const { brand, partner } = useSiteContext();

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      const { ReactComponent } = await import(`@eti/assets/logos/meta-partners/${partner}.svg`);

      if (isMounted) {
        setPartnerLogo(<ReactComponent className={logoImage} role="img" />);
      }
    };

    const init = async () => {
      try {
        await load();
      } catch (err) {
        console.warn(err); // eslint-disable-line no-console
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [partner]);

  return (
    <div className={wrapperStyles}>
      <div className={logoWrapperStyles} style={{ gridArea: 'from' }}>
        {partnerLogo}
      </div>

      <ThinArrow aria-hidden className={cx(arrowStyles, thinArrowStyles)} />
      <ThickArrow aria-hidden className={cx(arrowStyles, firstThickArrowStyles)} />
      <ThickArrow aria-hidden className={cx(arrowStyles, secondThickArrowStyles)} />

      <div className={cx(logoWrapperStyles, lastBoxStyles)}>
        <BrandLogo brand={brand.code} className={logoImage} shouldUseContrast />
      </div>
    </div>
  );
};

export default ProgressiveBackgroundSteps;
